import { useCallback, useEffect, useState } from "react";
import { format, parseISO } from "date-fns";
import api from "./api";
import { Card, Empty, PageTitle, SecondaryButton } from "./ui";

const PAGE_SIZE = 30;

const EVENT_KINDS = {
    restaurant: "Feier im Restaurant",
    catering: "Catering",
};

const RequestCard = ({ request: q }) => {
    const isEvent = q.type === "feier";
    const name = isEvent ? q.name : `${q.firstName} ${q.lastName}`;
    const details = isEvent
        ? [
              EVENT_KINDS[q.eventKind],
              q.occasion,
              q.eventDate && format(parseISO(q.eventDate), "dd.MM.yyyy"),
              q.guests && `${q.guests} Personen`,
          ].filter(Boolean)
        : [];

    return (
        <Card>
            <div className="flex justify-between gap-3">
                <div className="text-[17px]">
                    {name}
                    <span
                        className={`ml-2 border px-1.5 py-0.5 align-middle text-[11px] font-semibold uppercase tracking-[.1em] ${
                            isEvent ? "border-messing text-messing" : "border-forest/30 text-forest/60"
                        }`}
                    >
                        {isEvent ? "Feier" : "Kontakt"}
                    </span>
                </div>
                <div className="whitespace-nowrap text-xs text-forest/55">
                    {format(new Date(q.createdAt), "dd.MM.yyyy HH:mm")}
                </div>
            </div>
            <div className="mt-0.5 break-words text-[13px] text-forest/60">
                {[q.email, q.phoneNumber].filter(Boolean).join(" · ")}
            </div>
            {details.length > 0 && <div className="mt-2 text-sm">{details.join(" · ")}</div>}
            {q.message && <p className="mt-3 mb-0 whitespace-pre-line text-[15px] leading-[1.6]">{q.message}</p>}
            <div className="mt-2.5 flex gap-4 text-[13px]">
                {q.email && (
                    <a href={`mailto:${q.email}`} className="py-1.5 underline">
                        Antworten
                    </a>
                )}
                {q.phoneNumber && (
                    <a href={`tel:${q.phoneNumber}`} className="py-1.5 underline">
                        Anrufen
                    </a>
                )}
            </div>
        </Card>
    );
};

export default function RequestsPage() {
    const [requests, setRequests] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const load = useCallback(async (skip) => {
        setLoading(true);
        try {
            const response = await api.get("/contact", { params: { skip, limit: PAGE_SIZE } });
            setRequests((prev) => {
                if (skip === 0) return response.data.requests;
                // Neue Anfragen zwischendurch verschieben die Seiten; Doppelte auslassen.
                const known = new Set(prev.map((q) => q._id));
                return [...prev, ...response.data.requests.filter((q) => !known.has(q._id))];
            });
            setTotal(response.data.total);
            setError(false);
        } catch (err) {
            setError(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load(0);
    }, [load]);

    return (
        <div>
            <PageTitle aside={total ? `${total} insgesamt` : null}>Anfragen</PageTitle>
            <div className="grid gap-2.5">
                {requests.map((q) => (
                    <RequestCard key={q._id} request={q} />
                ))}
                {!loading && error && <Empty>Anfragen konnten nicht geladen werden.</Empty>}
                {!loading && !error && requests.length === 0 && <Empty>Noch keine Anfragen.</Empty>}
                {requests.length < total && (
                    <SecondaryButton disabled={loading} onClick={() => load(requests.length)}>
                        {loading ? "Lädt …" : "Mehr laden"}
                    </SecondaryButton>
                )}
            </div>
        </div>
    );
}
